import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "../../styles/pages/games/GeoCapitals.module.scss";


// ---------------------- Data (extend anytime) ----------------------
const COUNTRIES = [
  // Europe
  { c: "France", cap: "Paris", flag: "🇫🇷", region: "Europe" },
  { c: "Germany", cap: "Berlin", flag: "🇩🇪", region: "Europe" },
  { c: "Italy", cap: "Rome", flag: "🇮🇹", region: "Europe" },
  { c: "Spain", cap: "Madrid", flag: "🇪🇸", region: "Europe" },
  { c: "Portugal", cap: "Lisbon", flag: "🇵🇹", region: "Europe" },
  { c: "Netherlands", cap: "Amsterdam", flag: "🇳🇱", region: "Europe" },
  { c: "Belgium", cap: "Brussels", flag: "🇧🇪", region: "Europe" },
  { c: "Sweden", cap: "Stockholm", flag: "🇸🇪", region: "Europe" },
  { c: "Norway", cap: "Oslo", flag: "🇳🇴", region: "Europe" },
  { c: "Denmark", cap: "Copenhagen", flag: "🇩🇰", region: "Europe" },
  { c: "Poland", cap: "Warsaw", flag: "🇵🇱", region: "Europe" },
  { c: "Greece", cap: "Athens", flag: "🇬🇷", region: "Europe" },
  { c: "Switzerland", cap: "Bern", flag: "🇨🇭", region: "Europe" },
  { c: "Austria", cap: "Vienna", flag: "🇦🇹", region: "Europe" },
  { c: "Ireland", cap: "Dublin", flag: "🇮🇪", region: "Europe" },
  { c: "United Kingdom", cap: "London", flag: "🇬🇧", region: "Europe" },
  { c: "Czechia", cap: "Prague", flag: "🇨🇿", region: "Europe" },
  { c: "Hungary", cap: "Budapest", flag: "🇭🇺", region: "Europe" },
  { c: "Finland", cap: "Helsinki", flag: "🇫🇮", region: "Europe" },
  // Americas
  { c: "United States", cap: "Washington, D.C.", flag: "🇺🇸", region: "Americas" },
  { c: "Canada", cap: "Ottawa", flag: "🇨🇦", region: "Americas" },
  { c: "Mexico", cap: "Mexico City", flag: "🇲🇽", region: "Americas" },
  { c: "Brazil", cap: "Brasília", flag: "🇧🇷", region: "Americas" },
  { c: "Argentina", cap: "Buenos Aires", flag: "🇦🇷", region: "Americas" },
  { c: "Chile", cap: "Santiago", flag: "🇨🇱", region: "Americas" },
  { c: "Peru", cap: "Lima", flag: "🇵🇪", region: "Americas" },
  { c: "Colombia", cap: "Bogotá", flag: "🇨🇴", region: "Americas" },
  { c: "Cuba", cap: "Havana", flag: "🇨🇺", region: "Americas" },
  // Africa
  { c: "Nigeria", cap: "Abuja", flag: "🇳🇬", region: "Africa" },
  { c: "Egypt", cap: "Cairo", flag: "🇪🇬", region: "Africa" },
  { c: "Kenya", cap: "Nairobi", flag: "🇰🇪", region: "Africa" },
  { c: "Ethiopia", cap: "Addis Ababa", flag: "🇪🇹", region: "Africa" },
  { c: "South Africa", cap: "Pretoria", flag: "🇿🇦", region: "Africa" },
  { c: "Ghana", cap: "Accra", flag: "🇬🇭", region: "Africa" },
  { c: "Morocco", cap: "Rabat", flag: "🇲🇦", region: "Africa" },
  { c: "Algeria", cap: "Algiers", flag: "🇩🇿", region: "Africa" },
  // Asia
  { c: "Japan", cap: "Tokyo", flag: "🇯🇵", region: "Asia" },
  { c: "China", cap: "Beijing", flag: "🇨🇳", region: "Asia" },
  { c: "India", cap: "New Delhi", flag: "🇮🇳", region: "Asia" },
  { c: "South Korea", cap: "Seoul", flag: "🇰🇷", region: "Asia" },
  { c: "Indonesia", cap: "Jakarta", flag: "🇮🇩", region: "Asia" },
  { c: "Thailand", cap: "Bangkok", flag: "🇹🇭", region: "Asia" },
  { c: "Vietnam", cap: "Hanoi", flag: "🇻🇳", region: "Asia" },
  { c: "Philippines", cap: "Manila", flag: "🇵🇭", region: "Asia" },
  { c: "Malaysia", cap: "Kuala Lumpur", flag: "🇲🇾", region: "Asia" },
  { c: "Pakistan", cap: "Islamabad", flag: "🇵🇰", region: "Asia" },
  { c: "Saudi Arabia", cap: "Riyadh", flag: "🇸🇦", region: "Asia" },
  // Oceania
  { c: "Australia", cap: "Canberra", flag: "🇦🇺", region: "Oceania" },
  { c: "New Zealand", cap: "Wellington", flag: "🇳🇿", region: "Oceania" },
];

const LETTERS = ["A", "B", "C", "D"];

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = (Math.random() * (i + 1)) | 0;
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
function sampleN(arr, n) {
  return shuffle(arr).slice(0, n);
}
function distractors(all, correct, region) {
  // Prefer wrong options from same region for realism
  const pool = all.filter((x) => x.cap !== correct && (x.region === region));
  const extra = all.filter((x) => x.cap !== correct && x.region !== region);
  const chosen = sampleN(pool.length >= 6 ? pool : pool.concat(extra), 3).map((x) => x.cap);
  return chosen;
}

// ---------------------- Sound FX ----------------------
function useSfx() {
  const ref = useRef({ ctx: null, g: null, on: true });
  function setup() {
    if (ref.current.ctx) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    const ctx = new AC();
    const g = ctx.createGain();
    g.gain.value = 0.14;
    g.connect(ctx.destination);
    ref.current.ctx = ctx;
    ref.current.g = g;
  }
  function ding(f = 680, d = 0.08, type = "triangle", vol = 1) {
    if (!ref.current.on) return;
    if (!ref.current.ctx) setup();
    const o = ref.current.ctx.createOscillator();
    const gg = ref.current.ctx.createGain();
    o.type = type;
    o.frequency.value = f;
    o.connect(gg);
    gg.connect(ref.current.g);
    const t = ref.current.ctx.currentTime;
    gg.gain.value = 0.001;
    gg.gain.linearRampToValueAtTime(0.14 * vol, t + 0.01);
    gg.gain.exponentialRampToValueAtTime(0.001, t + d);
    o.start(t);
    o.stop(t + d + 0.02);
  }
  const sfx = {
    stamp: () => ding(520, 0.07, "triangle", 1),
    buzz: () => ding(180, 0.12, "square", 1),
    complete: () => [620, 820, 980].forEach((f, i) => setTimeout(() => ding(f, 0.09, "sawtooth", 1), i * 90)),
    toggle: () => { ref.current.on = !ref.current.on; ding(720, 0.05, "triangle", 0.8); },
    isOn: () => ref.current.on,
  };
  return sfx;
}

// ---------------------- Paper-plane particles ----------------------
function usePlanes() {
  const canvasRef = useRef(null);
  const planes = useRef([]);
  const raf = useRef(0);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");

    const loop = () => {
      const dpr = window.devicePixelRatio || 1;
      if (c.width !== c.clientWidth * dpr || c.height !== c.clientHeight * dpr) {
        c.width = c.clientWidth * dpr;
        c.height = c.clientHeight * dpr;
      }
      ctx.clearRect(0, 0, c.width, c.height);

      for (let i = planes.current.length - 1; i >= 0; i--) {
        const p = planes.current[i];
        p.x += p.vx;
        p.y += p.vy;
        p.vy *= 0.99; // tiny drag
        p.life -= 16;
        if (p.life <= 0) {
          planes.current.splice(i, 1);
          continue;
        }
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.globalAlpha = Math.max(0, Math.min(1, p.life / 800));
        // simple "paper plane" triangle
        ctx.fillStyle = "#f3f7ff";
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(18, 4);
        ctx.lineTo(1, -6);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
        p.rot += 0.01;
      }
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf.current);
  }, []);

  function burst(count = 16) {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = c.clientWidth * dpr;
    c.height = c.clientHeight * dpr;
    for (let i = 0; i < count; i++) {
      const ang = Math.random() * Math.PI * 2;
      const sp = 1.5 + Math.random() * 2.5;
      planes.current.push({
        x: (c.width / 2) + Math.cos(ang) * 10,
        y: (c.height * 0.15) + Math.sin(ang) * 10,
        vx: Math.cos(ang) * sp,
        vy: Math.sin(ang) * sp - 0.4,
        rot: Math.random() * 0.5 - 0.25,
        life: 1200 + Math.random() * 900,
      });
    }
  }
  return { canvasRef, burst };
}

// ---------------------- Component ----------------------
export default function PassportCapitals() {
  const sfx = useSfx();
  const planes = usePlanes();

  // Build 10 questions: {country, options[], correctIndex}
  const [pack, setPack] = useState(() => makePack());
  const [i, setI] = useState(0);
  const [choice, setChoice] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [score, setScore] = useState(0);
  const [best, setBest] = useState(Number(localStorage.getItem("cap_best") || 0));
  const [done, setDone] = useState(false);
  const [hintLeft, setHintLeft] = useState(1);
  const [shake, setShake] = useState(false);

  function makePack() {
    const qCountries = sampleN(COUNTRIES, 10);
    return qCountries.map((row) => {
      const wrong = distractors(COUNTRIES, row.cap, row.region);
      const opts = shuffle([row.cap, ...wrong]);
      const correctIndex = opts.indexOf(row.cap);
      return { row, opts, correctIndex };
    });
  }

  const current = pack[i];

  function answer(idx) {
    if (revealed || done) return;
    setChoice(idx);
    setRevealed(true);
    if (idx === current.correctIndex) {
      setScore((s) => s + 100);
      sfx.stamp();
    } else {
      sfx.buzz();
      setShake(true);
      setTimeout(() => setShake(false), 250);
    }
  }

  function next() {
    if (!revealed) return;
    if (i + 1 >= pack.length) {
      setDone(true);
      setBest((b) => {
        const next = Math.max(b, score);
        localStorage.setItem("cap_best", String(next));
        return next;
      });
      if (score >= 800) planes.burst(24);
      sfx.complete();
      return;
    }
    setI((x) => x + 1);
    setChoice(null);
    setRevealed(false);
  }

  function shuffleNew() {
    setPack(makePack());
    setI(0); setChoice(null); setRevealed(false);
    setScore(0); setDone(false);
    setHintLeft(1);
  }

  // Keyboard shortcuts
  useEffect(() => {
    function onKey(e) {
      if (done) return;
      if (!revealed) {
        if (["1", "2", "3", "4"].includes(e.key)) answer(Number(e.key) - 1);
      } else if (e.key === "Enter") {
        next();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [revealed, done, current]); // eslint-disable-line

  const progress = Math.round((i / pack.length) * 100);

  return (
    <div className={styles.wrap}>
      <header className={styles.top}>
        <div className={styles.title}>Passport Quest — Capitals</div>
        <div className={styles.stats}>
          <span className={styles.chip}>Score: {score}</span>
          <span className={styles.chip}>Best: {best}</span>
          <button className={styles.btn} onClick={sfx.toggle}>{sfx.isOn() ? "🔊" : "🔇"}</button>
          <button className={styles.btn} onClick={shuffleNew}>New Flight</button>
          <button
            className={`${styles.btn} ${hintLeft ? "" : styles.btnOff}`}
            onClick={() => hintLeft && setHintLeft(hintLeft - 1)}
            title="Show region hint"
          >
            🧭 Hint {hintLeft ? "(1)" : "(0)"}
          </button>
        </div>
      </header>

      <main className={styles.stage}>
        {/* paper planes */}
        <div className={styles.fxLayer}><canvas ref={planes.canvasRef} className={styles.fxCanvas} /></div>

        <div className={styles.progress}><div className={styles.progressFill} style={{ width: `${progress}%` }} /></div>

        <section className={`${styles.card} ${shake ? styles.shake : ""}`}>
          <div className={styles.passHeader}>
            <div className={styles.flag}>{current.row.flag}</div>
            <div className={styles.country}>
              <div className={styles.label}>Country</div>
              <div className={styles.countryName}>{current.row.c}</div>
            </div>
            <div className={styles.stampArea}>
              {revealed ? (
                choice === current.correctIndex ? (
                  <div className={`${styles.stamp} ${styles.approved}`}>CORRECT</div>
                ) : (
                  <div className={`${styles.stamp} ${styles.denied}`}>MISSED</div>
                )
              ) : null}
            </div>
          </div>

          <div className={styles.prompt}>What is the capital?</div>
          {hintLeft === 0 && !revealed && (
            <div className={styles.hint}>Region: <b>{current.row.region}</b></div>
          )}

          <div className={styles.options}>
            {current.opts.map((opt, idx) => {
              const isCorrect = idx === current.correctIndex;
              const isChosen = choice === idx;
              const cls = [
                styles.opt,
                revealed && isCorrect ? styles.correct : "",
                revealed && isChosen && !isCorrect ? styles.wrong : ""
              ].join(" ");
              return (
                <button
                  key={idx}
                  className={cls}
                  onClick={() => answer(idx)}
                  disabled={revealed}
                  aria-label={`Answer ${idx + 1}`}
                >
                  <span className={styles.letter}>{LETTERS[idx]}</span>
                  <span className={styles.optText}>{opt}</span>
                </button>
              );
            })}
          </div>

          <div className={styles.footer}>
            {!revealed ? (
              <div className={styles.tip}>Press <b>1–4</b> to answer</div>
            ) : (
              <button className={styles.next} onClick={next}>Next ▷ (Enter)</button>
            )}
          </div>
        </section>

        {done && (
          <div className={styles.result}>
            <h3 className={styles.resultTitle}>{score >= 800 ? "Frequent Flyer! ✈️" : score >= 600 ? "Nice Trip!" : "Boarding Again?"}</h3>
            <p className={styles.resultSub}>Final score: <b>{score}</b> • Best: <b>{Math.max(best, score)}</b></p>
            <div className={styles.resultActions}><button onClick={shuffleNew}>New Flight</button></div>
          </div>
        )}
      </main>
    </div>
  );
}
